import { useState, useEffect, useRef } from 'react';
import { EternityPortal } from './components/EternityPortal';
import { HALO_CONFIGS } from './engine/halo-configs';
import { PORTAL_PRESETS } from './effects/presets';
import { DEFAULT_PORTALS, type PortalConfigData } from './lib/config-store';

function App() {
  const portalWidth = Math.min(window.innerWidth * 0.52, 380);

  const [portals, setPortals] = useState<PortalConfigData[]>(DEFAULT_PORTALS);

  useEffect(() => {
    fetch('/portal-config.json')
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (Array.isArray(data)) setPortals(data); })
      .catch(() => {});
  }, []);

  // ── Scroll-linked horizontal gallery ──
  // Vertical scroll progress within the tall section drives horizontal scroll.
  const sectionRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scroller = scrollRef.current;
    const section = sectionRef.current;
    if (!scroller || !section) return;

    const onScroll = () => {
      const sectionTop = section.offsetTop;
      const sectionHeight = section.offsetHeight;
      const viewportHeight = window.innerHeight;
      const scrollY = window.scrollY;

      // How far into the section we've scrolled (0 = just entered, 1 = leaving)
      const scrollableDistance = sectionHeight - viewportHeight;
      if (scrollableDistance <= 0) return;

      const progress = Math.max(0, Math.min(1, (scrollY - sectionTop) / scrollableDistance));
      const maxScrollLeft = scroller.scrollWidth - scroller.clientWidth;
      scroller.scrollLeft = progress * maxScrollLeft;
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [portals]);

  return (
    <>
      {/* Gold UI Frame */}
      <div className="gold-frame" aria-hidden="true">
        <div className="frame-top" />
        <div className="frame-right" />
        <div className="frame-bottom" />
        <div className="frame-left" />
      </div>

      {/* I. Hero */}
      <section
        style={{
          position: 'relative',
          zIndex: 10,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '4rem 2rem',
          textAlign: 'center',
        }}
      >
        <h1
          style={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontSize: 'clamp(2.5rem, 7vw, 5.5rem)',
            fontWeight: 400,
            color: '#C5A258',
            letterSpacing: '0.06em',
            lineHeight: 1.1,
            animation: 'fadeIn 1.5s ease both',
          }}
        >
          Websites of Eternity
        </h1>
        <p
          style={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontSize: 'clamp(0.95rem, 2vw, 1.35rem)',
            fontWeight: 400,
            color: 'rgba(245, 240, 232, 0.6)',
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            marginTop: '1.25rem',
            animation: 'fadeIn 1.5s ease 0.3s both',
          }}
        >
          A Trinity Agency Portfolio Series
        </p>
        <p
          style={{
            fontFamily: "'Noto Serif', Georgia, serif",
            fontSize: 'clamp(0.875rem, 1.5vw, 1.0625rem)',
            fontStyle: 'italic',
            color: 'rgba(245, 240, 232, 0.45)',
            lineHeight: 1.7,
            maxWidth: 600,
            marginTop: '2.5rem',
            animation: 'fadeIn 1.5s ease 0.6s both',
          }}
        >
          Not paintings but portals. Not art but thresholds
          <br />
          between the temporal and the eternal.
        </p>
      </section>

      {/* II. The Premise */}
      <section
        style={{
          position: 'relative',
          zIndex: 10,
          padding: '6rem 2rem',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          animation: 'fadeInSubtle 2s ease 1s both',
        }}
      >
        <div style={{ maxWidth: 720, width: '100%', display: 'flex', flexDirection: 'column', gap: '1.75rem' }}>
          <p style={proseStyle}>
            In Orthodox theology, an icon is never merely painted — it is <em style={{ color: '#F5D799' }}>written</em>.
            It does not depict a person. It makes them <em style={{ color: '#F5D799' }}>present</em>.
            The golden background is not decoration — it is the uncreated light of God
            dissolving the boundary between now and forever.
          </p>
          <p style={proseStyle}>
            <strong style={{ color: '#C5A258', fontWeight: 600 }}>Websites of Eternity</strong> translates this
            principle into the digital medium. A website is not a biography. It is an icon written in code — a
            threshold where a life becomes present again, navigable, alive.
          </p>
          <p style={proseStyle}>
            Each Website of Eternity is a sovereign, self-contained web experience that makes a life present again.
            The medium is new. The function is ancient.
          </p>
          <hr
            style={{
              width: 60,
              height: 1,
              backgroundColor: 'rgba(197, 162, 88, 0.3)',
              border: 'none',
              margin: '3rem auto 0',
            }}
          />
        </div>
      </section>

      {/* III. The Collection — scroll-linked horizontal gallery */}
      {/* Each portal occupies one full viewport width, centered. */}
      {/* One vertical scroll-step (100vh) advances exactly one portal. */}
      <div
        ref={sectionRef}
        style={{
          position: 'relative',
          zIndex: 10,
          height: `${portals.length * 100}vh`,
        }}
      >
        <div
          style={{
            position: 'sticky',
            top: 0,
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
          }}
        >
          <div
            ref={scrollRef}
            className="portal-scroll"
            style={{
              display: 'flex',
              overflowX: 'hidden',
              width: '100vw',
              boxSizing: 'border-box',
              alignItems: 'center',
            }}
          >
            {portals.map(p => (
              <div
                key={p.id}
                style={{
                  flexShrink: 0,
                  width: '100vw',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <EternityPortal
                  imageSrc={p.imageSrc}
                  name={p.name}
                  subtitle={p.subtitle}
                  href={p.id === 'makarios' ? '/makarios/' : undefined}
                  width={portalWidth}
                  heightRatio={1.45}
                  archPointedness={p.archPointedness}
                  archStart={p.archStart}
                  frameThickness={p.frameThickness}
                  haloConfig={HALO_CONFIGS[p.haloConfigKey]}
                  pinealGland={p.pinealGland}
                  haloTranslate={p.haloTranslate}
                  haloScale={p.haloScale}
                  imageScale={p.imageScale}
                  imageOffsetX={p.imageOffsetX}
                  fadeStart={p.fadeStart}
                  portalEffect={PORTAL_PRESETS[p.portalPresetKey]}
                  alt={p.name}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* IV. The Closing — Rublev's Trinity */}
      <section
        style={{
          position: 'relative',
          zIndex: 10,
          padding: '8rem 2rem 4rem',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <div style={{ maxWidth: 600, width: '100%', margin: '0 auto 4rem' }}>
          <img
            src="/images/rublev_large.jpg"
            alt="The Trinity — Andrei Rublev, circa 1411."
            width={600}
            height={750}
            loading="lazy"
            style={{ width: '100%', height: 'auto', border: '1px solid rgba(197, 162, 88, 0.3)' }}
          />
        </div>
        <div
          style={{
            maxWidth: 640,
            width: '100%',
            margin: '0 auto',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            gap: '1.5rem',
          }}
        >
          <p
            style={{
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              fontSize: 'clamp(1.5rem, 3vw, 2rem)',
              fontStyle: 'italic',
              color: '#C5A258',
              letterSpacing: '0.08em',
            }}
          >
            &laquo;Троица&raquo;
          </p>
          <p style={{ fontFamily: "'Noto Serif', Georgia, serif", fontSize: '0.9375rem', color: 'rgba(245,240,232,0.5)' }}>
            Andrei Rublev, circa 1411
          </p>
          <p
            style={{
              fontFamily: "'Noto Serif', Georgia, serif",
              fontSize: '1.0625rem',
              lineHeight: 1.85,
              color: 'rgba(245,240,232,0.75)',
              marginTop: '1rem',
            }}
          >
            The icon that gave Trinity its name and Eternity its purpose. Three angels seated at the table of Abraham —
            the divine community in perfect unity. The golden background dissolves the boundary between heaven and earth,
            between the fifteenth century and today.
          </p>
          <p
            style={{
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              fontSize: 'clamp(1.125rem, 2vw, 1.375rem)',
              fontWeight: 600,
              color: '#C5A258',
              letterSpacing: '0.08em',
              marginTop: '0.5rem',
            }}
          >
            trinit-y. eternit-y.
          </p>
          <p
            style={{
              fontFamily: "'Noto Serif', Georgia, serif",
              fontSize: '1rem',
              fontStyle: 'italic',
              color: 'rgba(245,240,232,0.55)',
            }}
          >
            Same rhythm. Same ending. Eternity dwells within the Trinity.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer
        style={{
          position: 'relative',
          zIndex: 10,
          padding: '6rem 2rem 4rem',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '0.75rem',
        }}
      >
        <hr
          style={{ width: 40, height: 1, backgroundColor: 'rgba(197,162,88,0.2)', border: 'none', marginBottom: '2rem' }}
        />
        <a
          href="https://trinity.agency"
          target="_blank"
          rel="noopener"
          style={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontSize: '1rem',
            letterSpacing: '0.25em',
            textTransform: 'uppercase',
            color: 'rgba(197,162,88,0.5)',
          }}
        >
          Trinity Agency
        </a>
        <p
          style={{
            fontFamily: "'Noto Serif', Georgia, serif",
            fontSize: '0.8125rem',
            color: 'rgba(245,240,232,0.25)',
            letterSpacing: '0.1em',
          }}
        >
          Paphos, Cyprus
        </p>
      </footer>
    </>
  );
}

const proseStyle: React.CSSProperties = {
  fontFamily: "'Noto Serif', Georgia, serif",
  fontSize: '1.125rem',
  lineHeight: 1.85,
  color: 'rgba(245, 240, 232, 0.85)',
};

export default App;
